import React, { useState, useEffect, useRef } from "react";
import CytoscapeComponent from "react-cytoscapejs";
import {
  initialNodeStyle,
  initialDirectedEdgeStyle,
  initialUndirectedEdgeStyle,
} from "./GraphStyle";
import BFSAlgorithm from "./BFSAlgorithm";
import DFSAlgorithm from "./DFSAlgorithm";
import "./CytoscapeGraph.css";

const CytoscapeGraph = ({ algorithmType }) => {
  const cyStyle = {
    width: "100%",
    height: "100%",
  };

  const resetState = () => {
    setNodes([]);
    setEdges([]);
    setVisited([]);
    setQueue([]);
    setAdjacencyList({});
    setGenerateGraph(false);
  };

  // Initialize nodes, edges, visited, and queue using useState
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [visited, setVisited] = useState([]);
  const [queue, setQueue] = useState([]);
  const [cytoscapeReady, setCytoscapeReady] = useState(false); // Track Cytoscape readiness

  const [isDirected, setIsDirected] = useState(false); // Default to undirected graph
  const [numNodes, setNumNodes] = useState(2); // Number of nodes
  const [adjacencyList, setAdjacencyList] = useState({}); // Adjacency list
  const [generateGraph, setGenerateGraph] = useState(false);

  // Create a ref to store the Cytoscape instance
  const cytoscapeRef = useRef(null);

  useEffect(() => {
    if (generateGraph) {
      // Generate graph elements
      const generatedElements = generateGraphElements(numNodes, adjacencyList);
      //console.log("Generated Elements:", generatedElements);
      setNodes(generatedElements.nodes);
      setEdges(generatedElements.edges);
      if (cytoscapeRef.current) {
        cytoscapeRef.current.fit();
      }
    }
  }, [generateGraph, numNodes, adjacencyList]);

  const generateGraphElements = (numNodes, adjacencyList) => {
    const generatedNodes = [];
    const generatedEdges = [];

    // Calculate the number of rows and columns for the grid
    const numRows = Math.ceil(Math.sqrt(numNodes));
    const numCols = Math.ceil(numNodes / numRows);

    // Calculate the spacing between nodes
    const spacingX = 300 / numCols;
    const spacingY = 300 / numRows;

    // Generate nodes in a grid
    for (let row = 0; row < numRows; row++) {
      for (let col = 0; col < numCols; col++) {
        const nodeId = row * numCols + col + 1;
        if (nodeId <= numNodes) {
          // Check if nodeId is within the desired range
          const positionY = row * spacingY + (col % 2 === 0 ? 0 : spacingY / 2);
          generatedNodes.push({
            data: { id: `node${nodeId}`, label: `${nodeId}` },
            position: { x: col * spacingX, y: positionY },
          });
        }
      }
    }

    // Generate edges based on the user's adjacency list
    for (const sourceNode in adjacencyList) {
      const targetNodes = adjacencyList[sourceNode];
      targetNodes.forEach((targetNode) => {
        if (isDirected || sourceNode < targetNode) {
          generatedEdges.push({
            data: {
              id: `edge${sourceNode}-${targetNode}`,
              source: `node${sourceNode}`,
              target: `node${targetNode}`,
            },
          });
        }
      });
    }

    return { nodes: generatedNodes, edges: generatedEdges };
  };

  const handleDirectedChange = () => {
    setIsDirected(!isDirected);
  };

  const handleNumNodesChange = (e) => {
    const newNumNodes = parseInt(e.target.value);

    // Ensure that the number of nodes is within the desired range
    if (newNumNodes >= 2 && newNumNodes <= 10) {
      setNumNodes(newNumNodes);
    } else if (newNumNodes < 2) {
      setNumNodes(2); // Minimum value of 2
    } else if (newNumNodes > 10) {
      setNumNodes(10); // Maximum value of 10
    }
  };

  const handleAdjacencyChange = (node, adjacentNode) => {
    const updatedAdjacencyList = { ...adjacencyList };

    if (!updatedAdjacencyList[node]) {
      updatedAdjacencyList[node] = [];
    }
    if (!updatedAdjacencyList[adjacentNode]) {
      updatedAdjacencyList[adjacentNode] = [];
    }

    if (!isDirected) {
      // For undirected graphs, update adjacency status for both nodes
      const nodeIndex = updatedAdjacencyList[node].indexOf(adjacentNode);
      const adjacentNodeIndex =
        updatedAdjacencyList[adjacentNode].indexOf(node);

      if (nodeIndex !== -1) {
        updatedAdjacencyList[node].splice(nodeIndex, 1);
        updatedAdjacencyList[adjacentNode].splice(adjacentNodeIndex, 1);
      } else {
        updatedAdjacencyList[node].push(adjacentNode);
        updatedAdjacencyList[adjacentNode].push(node);
      }
    } else {
      // For directed graphs, only update the adjacency status for the selected node
      const nodeIndex = updatedAdjacencyList[node].indexOf(adjacentNode);

      if (nodeIndex !== -1) {
        updatedAdjacencyList[node].splice(nodeIndex, 1);
      } else {
        updatedAdjacencyList[node].push(adjacentNode);
      }
    }

    setAdjacencyList(updatedAdjacencyList);
  };

  const handleGenerateGraph = () => {
    setNodes([]);
    setEdges([]);
    //console.log("Generate Graph button clicked");
    //console.log("Before state change:", generateGraph);
    setGenerateGraph((prevGenerateGraph) => {
      if (!prevGenerateGraph) {
        // Clear existing nodes and edges
        setNodes([]);
        setEdges([]);

        const generatedElements = generateGraphElements(
          numNodes,
          adjacencyList
        );
        //console.log("Generated Elements:", generatedElements);
        setNodes(generatedElements.nodes);
        setEdges(generatedElements.edges);
      }
      return !prevGenerateGraph;
    });
  };

  const edgeStyle = isDirected
    ? initialDirectedEdgeStyle
    : initialUndirectedEdgeStyle;
  const handleCytoscapeReady = (cy) => {
    // Ensure that Cytoscape is fully initialized before setting cytoscapeReady to true
    if (cy) {
      cy.style()
        .fromJson([
          {
            selector: "node",
            style: {
              ...initialNodeStyle,
            },
          },
          {
            selector: "edge",
            style: {
              ...edgeStyle,
            },
          },
        ])
        .update();

      setCytoscapeReady(true);
    }
  };

  return (
    <div className="graph-container">
      <div className="left-column">
        <div className="graph-options">
          <label className="graph-option">
            <input
              type="radio"
              name="graphType"
              value="directed"
              checked={isDirected}
              onChange={handleDirectedChange}
            />{" "}
            &nbsp;Directed
          </label>
          <label className="graph-option">
            <input
              type="radio"
              name="graphType"
              value="undirected"
              checked={!isDirected}
              onChange={handleDirectedChange}
            />{" "}
            &nbsp;Undirected
          </label>
        </div>
        <div className="node-input">
          <label className="node-label">Number of Nodes:</label>
          <input
            className="node-input-field"
            type="number"
            value={numNodes}
            onChange={handleNumNodesChange}
            min="2"
            max="10"
          />
        </div>
        <div className="adjacency-list">
          <label className="adjacency-label">Adjacency List:</label>
          <ul className="adjacency-list-ul">
            {Array.from({ length: numNodes }, (_, i) => i + 1).map((node) => (
              <li key={node}>
                Node {node}:&nbsp;&nbsp;&nbsp;
                {Array.from({ length: numNodes }, (_, i) => i + 1)
                  .filter((adjNode) => adjNode !== node)
                  .map((adjNode) => (
                    <label key={adjNode} className="adjacency-list-label">
                      <input
                        type="checkbox"
                        checked={(adjacencyList[node] || []).includes(adjNode)}
                        onChange={() => handleAdjacencyChange(node, adjNode)}
                      />
                      {adjNode}
                    </label>
                  ))}
              </li>
            ))}
          </ul>
        </div>
        <div className="graph-controls">
          <button
            className="generate-reset-button"
            onClick={handleGenerateGraph}
          >
            Generate Graph
          </button>
          &nbsp;&nbsp;
          <button className="generate-reset-button" onClick={resetState}>
            Reset Graph
          </button>
        </div>
      </div>
      <div className="right-column">
        <div className="cytoscape-container ">
          <CytoscapeComponent
            elements={nodes.concat(edges)}
            style={cyStyle}
            cy={(cy) => {
              cy.userZoomingEnabled(false);
              cytoscapeRef.current = cy;
              handleCytoscapeReady(cy);
            }}
          />
        </div>
        {algorithmType === "bfs" && (
          <BFSAlgorithm
            cytoscape={cytoscapeRef.current}
            nodes={nodes}
            visited={visited}
            queue={queue}
            setVisited={setVisited}
            setQueue={setQueue}
            isDirected={isDirected}
          />
        )}
        {algorithmType === "dfs" && (
          <DFSAlgorithm
            cytoscape={cytoscapeRef.current}
            nodes={nodes}
            visited={visited}
            queue={queue}
            setVisited={setVisited}
            setQueue={setQueue}
            isDirected={isDirected}
          />
        )}
      </div>
    </div>
  );
};

export default CytoscapeGraph;
